import { useCallback, useEffect, useState, useRef } from 'react'
import { API } from '../../api'
import { TADMembership } from '../../type'
import { SharedGroupTypes } from '../../component/admin/ad-management/shared-groups/SharedGroupsList'
import { handleArrayBufferBackendError } from '../../utility/array-buffer-toast-handling'

export const LAST_MEMBERSHIPS_TAB_ACCESSED = 'lastMembershipsTabAccessed'

type TUseSharedGroups = {
  isLoading: boolean
  sharedGroups: TADMembership[]
  search: string
  setSearch: (search: string) => void
  page: number
  setPage: (page: number) => void
  totalCount: number
  maxPageSize: number
  totalPages: number
  getSharedGroups: () => void
  setSharedGroups: (sharedGroup: TADMembership[]) => void
  handleExportSharedGroupsButtonClick: () => void
}

export const useSharedGroups = (type: SharedGroupTypes): TUseSharedGroups => {
  const [isLoading, setIsLoading] = useState(true)
  const [sharedGroups, setSharedGroups] = useState<TADMembership[]>([])
  const [search, setSearch] = useState('')
  const [sharedGroupsPage, setSharedGroupsPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [maxPageSize, setMaxPageSize] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const abortControllerRef = useRef<AbortController | null>(null)
  const requestIdRef = useRef(0)

  const getUrl = () => {
    switch (type) {
      case SharedGroupTypes.ce:
        return 'ce/shared_groups/'
      case SharedGroupTypes.qlik:
        return 'qlik/shared_groups/'
      case SharedGroupTypes.pharmacy:
        return 'pharmacies/'
      case SharedGroupTypes.distributor:
        return 'distributors/'
      case SharedGroupTypes.omit:
        return 'omissions/'
      case SharedGroupTypes.referral_type:
        return 'referral_types/'
      case SharedGroupTypes.reports:
        return 'qlik_based_reports/'
      default:
        return ''
    }
  }

  const checkSharedGroupParam = () => {
    switch (type) {
      case SharedGroupTypes.pharmacy:
        return 'only'
      case SharedGroupTypes.distributor:
        return 'only'
      case SharedGroupTypes.omit:
        return 'only'
      case SharedGroupTypes.referral_type:
        return 'only'
      case SharedGroupTypes.reports:
        return 'only'
      default:
        return ''
    }
  }

  const getMemberships = useCallback(
    async (pageToFetch: number) => {
      if (!type) {
        return
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      abortControllerRef.current = new AbortController()
      setIsLoading(true)
      requestIdRef.current += 1
      const currentRequestId = requestIdRef.current
      try {
        const { max_page_size, total_pages, total_count, page_number, results } = await API.ADManagement.sharedGroups.list(
          search,
          pageToFetch,
          getUrl(),
          checkSharedGroupParam(),
          abortControllerRef.current.signal,
        )

        setMaxPageSize(Number(max_page_size))
        setTotalCount(Number(total_count))
        setTotalPages(Number(total_pages))
        setSharedGroups(results)
        setSharedGroupsPage(Number(page_number))
      } catch (error) {
        setSharedGroups([])
        setSharedGroupsPage(1)
        if ((error as { name?: string }).name === 'AbortError') {
          console.log('Request was aborted')
        } else {
          console.error('Request failed', error)
        }
      } finally {
        if (requestIdRef.current === currentRequestId) {
          setIsLoading(false)
        }
      }
    },
    [search, type],
  )

  useEffect(() => {
    setIsLoading(true)
    getMemberships(sharedGroupsPage)

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [getMemberships, sharedGroupsPage])

  const handlePageChange = useCallback((newPage: number) => {
    setSharedGroupsPage(newPage)
  }, [])

  const getExportUrl = (type: SharedGroupTypes): string => {
    switch (type) {
      case SharedGroupTypes.ce:
        return '/jarvis_api/groups/ce/export_to_excel/'
      case SharedGroupTypes.qlik:
        return '/jarvis_api/groups/qlik/apps/export_to_excel/'
      case SharedGroupTypes.pharmacy:
        return '/jarvis_api/groups/pharmacies/export_to_excel/'
      case SharedGroupTypes.distributor:
        return '/jarvis_api/groups/distributors/export_to_excel/'
      case SharedGroupTypes.omit:
        return '/jarvis_api/groups/omissions/export_to_excel/'
      case SharedGroupTypes.referral_type:
        return '/jarvis_api/groups/referral_types/export_to_excel/'
      case SharedGroupTypes.reports:
        return '/jarvis_api/groups/qlik_based_reports/export_to_excel/'
      default:
        return ''
    }
  }

  const handleExportSharedGroupsButtonClick = () => {
    const url = getExportUrl(type)
    if (url) {
      try {
        void API.ADManagement.sharedGroups.download(url, search)
      } catch (error) {
        handleArrayBufferBackendError(error)
      }
    }
  }

  return {
    isLoading,
    sharedGroups: sharedGroups,
    search,
    setSearch,
    page: sharedGroupsPage,
    setPage: handlePageChange,
    totalCount,
    maxPageSize,
    totalPages,
    getSharedGroups: () => getMemberships(sharedGroupsPage),
    setSharedGroups: setSharedGroups,
    handleExportSharedGroupsButtonClick,
  }
}
